// Pure serialisation helpers for the Estimate state.
//
// `sanitizeForSave` strips/normalises the in-memory shape into the payload
// expected by the backend (numbers coerced, missing nested defaults filled,
// new ids minted for missing rows). `hydrateFromDoc` inflates a backend doc
// back into the in-memory shape with sensible defaults for any plan blocks
// the server didn't return.
//
// Both functions are pure: no React, no axios, no state. Kept here so the
// massive `EstimateContext.jsx` orchestrator can shrink to state + actions.

import {
  buildEmptyDmMatrix,
  buildEmptyIllustrations,
  DEFAULT_ROLES,
  findCurrency,
  buildDefaultResourcePlan,
  buildDefaultBrexPlan,
  buildDefaultDmlPlan,
  buildDefaultRiskPlan,
  buildDefaultSchedulePlan,
  buildDefaultBidPlan,
  buildDefaultSbsPlan,
  buildDefaultReviewPlan,
} from "../lib/s1000dData";

const cleanLevel = (l) => ({ qty: Number(l?.qty) || 0, hours: Number(l?.hours) || 0 });

function _sanitizeDmMatrix(matrix) {
  const out = {};
  for (const [code, row] of Object.entries(matrix || {})) {
    out[code] = {
      simple: cleanLevel(row.simple),
      intermediate: cleanLevel(row.intermediate),
      complex: cleanLevel(row.complex),
    };
  }
  return out;
}

function _sanitizeSbsPlan(sbs) {
  return {
    systems: (sbs?.systems || []).map((s) => ({
      id: s.id || `sys-${Math.random().toString(36).slice(2)}`,
      name: s.name || "",
      generalQty: Number(s.generalQty) || 0,
      repairableQty: Number(s.repairableQty) || 0,
      subsystems: (s.subsystems || []).map((sub) => ({
        id: sub.id || `sub-${Math.random().toString(36).slice(2)}`,
        name: sub.name || "",
        generalQty: Number(sub.generalQty) || 0,
        repairableQty: Number(sub.repairableQty) || 0,
        subsubsystems: (sub.subsubsystems || []).map((ss) => ({
          id: ss.id || `ss-${Math.random().toString(36).slice(2)}`,
          name: ss.name || "",
          generalQty: Number(ss.generalQty) || 0,
          repairableQty: Number(ss.repairableQty) || 0,
        })),
      })),
    })),
    generalDmType: sbs?.generalDmType || "040",
    repairableDmType: sbs?.repairableDmType || "520",
    hoursPerItem: {
      simple: Number(sbs?.hoursPerItem?.simple) || 6,
      intermediate: Number(sbs?.hoursPerItem?.intermediate) || 8,
      complex: Number(sbs?.hoursPerItem?.complex) || 12,
    },
  };
}

export function sanitizeEstimateForSave(est, totals) {
  return {
    projectName: est.projectName || "Untitled",
    notes: est.notes || "",
    country: est.country || "AU",
    currency: est.currency || { ...findCurrency("AUD") },
    overheadPercent: Number(est.overheadPercent) || 0,
    contingencyPercent: Number(est.contingencyPercent) || 0,
    tags: Array.isArray(est.tags) ? est.tags : [],
    dmMatrix: _sanitizeDmMatrix(est.dmMatrix),
    illustrations: {
      simple: cleanLevel(est.illustrations.simple),
      intermediate: cleanLevel(est.illustrations.intermediate),
      complex: cleanLevel(est.illustrations.complex),
    },
    roles: est.roles.map((r) => ({
      name: r.name,
      hourlyRate: Number(r.hourlyRate) || 0,
      effortPercent: Number(r.effortPercent) || 0,
    })),
    resourcePlan: {
      startDate: est.resourcePlan?.startDate || "",
      endDate: est.resourcePlan?.endDate || "",
      locationPreset: est.resourcePlan?.locationPreset || "AU",
      weekendDays: Array.isArray(est.resourcePlan?.weekendDays) ? est.resourcePlan.weekendDays : [0, 6],
      publicHolidays: Number(est.resourcePlan?.publicHolidays) || 0,
      standardHoursPerDay: Number(est.resourcePlan?.standardHoursPerDay) || 8,
      effectiveHoursPerDay: Number(est.resourcePlan?.effectiveHoursPerDay) || 6,
    },
    brexPlan: {
      s1000dIssue: est.brexPlan?.s1000dIssue || "6.0",
      defineMinutesPerDP: Number(est.brexPlan?.defineMinutesPerDP) || 0,
      brexMinutesPerDP: Number(est.brexPlan?.brexMinutesPerDP) || 0,
      includeInProjectTotal: !!est.brexPlan?.includeInProjectTotal,
      categoryInclusions: est.brexPlan?.categoryInclusions || {},
      categoryDpOverrides: Object.fromEntries(
        Object.entries(est.brexPlan?.categoryDpOverrides || {}).map(([k, v]) => [k, Number(v) || 0])
      ),
      roles: (est.brexPlan?.roles || []).map((r) => ({
        name: r.name,
        hourlyRate: Number(r.hourlyRate) || 0,
        effortPercent: Number(r.effortPercent) || 0,
      })),
    },
    dmlPlan: est.dmlPlan || buildDefaultDmlPlan(),
    riskPlan: {
      risks: (est.riskPlan?.risks || []).map((r) => ({
        id: r.id || `r${Math.random().toString(36).slice(2)}`,
        title: r.title || "",
        category: r.category || "technical",
        likelihood: Number(r.likelihood) || 0,
        impact: Number(r.impact) || 0,
        mitigation: r.mitigation || "",
        ownerRole: r.ownerRole || "",
      })),
      assumptions: (est.riskPlan?.assumptions || []).map((a) => ({
        id: a.id || `a${Math.random().toString(36).slice(2)}`,
        text: a.text || "",
      })),
      dependencies: (est.riskPlan?.dependencies || []).map((d) => ({
        id: d.id || `d${Math.random().toString(36).slice(2)}`,
        text: d.text || "",
        type: d.type || "Internal",
      })),
      autoContingency: !!est.riskPlan?.autoContingency,
      maxAutoBumpPercent: Number(est.riskPlan?.maxAutoBumpPercent) || 20,
    },
    schedulePlan: {
      phases: Object.fromEntries(
        Object.entries(est.schedulePlan?.phases || {}).map(([k, v]) => [
          k,
          {
            percent: Number(v?.percent) || 0,
            startOffsetWeeks: Number(v?.startOffsetWeeks) || 0,
            durationWeeks: Number(v?.durationWeeks) || 0,
          },
        ])
      ),
      basisHours: est.schedulePlan?.basisHours || "combined",
    },
    bidPlan: {
      targetMarginPercent: Number(est.bidPlan?.targetMarginPercent) || 0,
      winProbabilityPercent: Number(est.bidPlan?.winProbabilityPercent) || 0,
      competitorPrice: Number(est.bidPlan?.competitorPrice) || 0,
      strategicAdjustmentPercent: Number(est.bidPlan?.strategicAdjustmentPercent) || 0,
      pricingBasis: est.bidPlan?.pricingBasis || "combined",
    },
    sbsPlan: _sanitizeSbsPlan(est.sbsPlan),
    reviewPlan: {
      percentages: Array.isArray(est.reviewPlan?.percentages) ? est.reviewPlan.percentages.map(Number) : [25, 50, 75],
    },
    totals: {
      baseCost: totals.baseCost,
      overheadCost: totals.overheadCost,
      contingencyCost: totals.contingencyCost,
      grandTotal: totals.grandTotal,
      totalCost: totals.grandTotal,
      totalHours: totals.totalHours,
      totalDms: totals.totalDms,
      totalIllus: totals.totalIllus,
    },
  };
}

export function hydrateEstimateFromDoc(d) {
  return {
    id: d.id,
    projectName: d.projectName || "",
    notes: d.notes || "",
    country: d.country || "AU",
    currency: d.currency || { ...findCurrency("AUD") },
    overheadPercent: Number(d.overheadPercent) || 0,
    contingencyPercent: Number(d.contingencyPercent) || 0,
    tags: Array.isArray(d.tags) ? d.tags : [],
    dmMatrix: { ...buildEmptyDmMatrix(), ...(d.dmMatrix || {}) },
    illustrations: { ...buildEmptyIllustrations(), ...(d.illustrations || {}) },
    roles: (d.roles && d.roles.length > 0) ? d.roles : DEFAULT_ROLES.map((r) => ({ ...r })),
    resourcePlan: { ...buildDefaultResourcePlan(), ...(d.resourcePlan || {}) },
    dmlPlan: { ...buildDefaultDmlPlan(), ...(d.dmlPlan || {}) },
    riskPlan: (() => {
      const base = buildDefaultRiskPlan();
      const incoming = d.riskPlan || {};
      return {
        ...base,
        ...incoming,
        risks: Array.isArray(incoming.risks) ? incoming.risks : base.risks,
        assumptions: Array.isArray(incoming.assumptions) ? incoming.assumptions : base.assumptions,
        dependencies: Array.isArray(incoming.dependencies) ? incoming.dependencies : base.dependencies,
      };
    })(),
    schedulePlan: (() => {
      const base = buildDefaultSchedulePlan();
      const incoming = d.schedulePlan || {};
      return {
        ...base,
        ...incoming,
        phases: { ...base.phases, ...(incoming.phases || {}) },
      };
    })(),
    bidPlan: { ...buildDefaultBidPlan(), ...(d.bidPlan || {}) },
    sbsPlan: (() => {
      const base = buildDefaultSbsPlan();
      const incoming = d.sbsPlan || {};
      return {
        ...base,
        ...incoming,
        systems: Array.isArray(incoming.systems) ? incoming.systems : base.systems,
        hoursPerItem: { ...base.hoursPerItem, ...(incoming.hoursPerItem || {}) },
      };
    })(),
    brexPlan: (() => {
      const base = buildDefaultBrexPlan();
      const incoming = d.brexPlan || {};
      return {
        ...base,
        ...incoming,
        categoryInclusions: { ...base.categoryInclusions, ...(incoming.categoryInclusions || {}) },
        categoryDpOverrides: { ...(incoming.categoryDpOverrides || {}) },
        roles: (incoming.roles && incoming.roles.length > 0) ? incoming.roles : base.roles,
      };
    })(),
    reviewPlan: { ...buildDefaultReviewPlan(), ...(d.reviewPlan || {}) },
  };
}
